import { MiraCompany, MiraCompanySave } from "@/data/models/MiraCompanyAdministration";
import { ClientAdministrationRow } from "@/data/models/ClientAdministrationRow";

/**
 * The logic behind the company list of the IRES Admin Client (#2610009 C4), kept out of the components so
 * it can be tested without mounting anything. IRES Cloud is the authority
 * (`MiraCompaniesForAdministration.ts`); these rules give the messages before a save is sent.
 */

/** The list's filters. */
export const MIRA_COMPANY_FILTERS = ["ACTIVE", "RETIRED", "ALL"] as const;
export type MiraCompanyFilter = typeof MIRA_COMPANY_FILTERS[number];

/** The fewest Mandanten a company names: one Mandant is a company already, unless it is a Master-Mandant. */
export const MIRA_COMPANY_MIN_CLIENTS = 2;

/** The Master-Mandanten: every group has a company IRES Cloud made for it, which names its master. */
export function groupMasterIds(companies: MiraCompany[]): Set<string> {
  return new Set(companies.map((company) => company.groupMasterId).filter((id): id is string => !!id));
}

/** Every Mandant a company covers, its group's members included where the list sent them. */
export function coveredClients(company: MiraCompany): string[] {
  return company.coveredClientIds ?? company.clientIds;
}

/** The dialog's editable fields. */
export interface MiraCompanyForm {
  name: string;
  clientIds: string[];
  retired: boolean;
  /** The Mandant that shows the company in Mira like a group, or null (#2610112 X6e). */
  viewingClientId: string | null;
}

/** The form for a company, or an empty one for a new company. */
export function toCompanyForm(company: MiraCompany | null): MiraCompanyForm {
  return {
    name: company?.name ?? "",
    clientIds: [...(company?.clientIds ?? [])],
    retired: company?.retired ?? false,
    viewingClientId: company?.viewingClientId ?? null,
  };
}

/**
 * The form with a new viewing client (#2610112 X6e). Chosen, it leaves the Mandanten: it looks at the
 * company, it is not part of it, and IRES Cloud refuses a viewing client that is one of them. The three
 * viewing clients made on 2026-10-29 were first listed among their company's Mandanten.
 */
export function withViewingClient(form: MiraCompanyForm, viewingClientId: string | null): MiraCompanyForm {
  return {
    ...form,
    viewingClientId,
    clientIds: viewingClientId ? form.clientIds.filter((clientId) => clientId !== viewingClientId) : form.clientIds,
  };
}

/** The Mandanten as IRES Cloud stores them: distinct, sorted. */
function normalisedIds(ids: string[]): string[] {
  return [...new Set(ids)].sort();
}

/** Whether the form differs from the stored company. */
export function companyFormChanged(stored: MiraCompany | null, form: MiraCompanyForm): boolean {
  const before = toCompanyForm(stored);
  return (
    form.name.trim() !== before.name ||
    normalisedIds(form.clientIds).join(",") !== normalisedIds(before.clientIds).join(",") ||
    form.retired !== before.retired ||
    form.viewingClientId !== before.viewingClientId
  );
}

/**
 * A field's problem, as an i18n key below `admin.miraCompanies.fieldErrors`.
 *
 * @param masters - the Master-Mandanten ({@link groupMasterIds}): one of them alone is a company.
 */
export function companyFieldError(field: "name" | "clientIds", form: MiraCompanyForm, masters: Set<string> = new Set()): string | null {
  if (field === "name") return form.name.trim() === "" ? "required" : null;
  const ids = normalisedIds(form.clientIds);
  return ids.length >= MIRA_COMPANY_MIN_CLIENTS || (ids.length === 1 && masters.has(ids[0])) ? null : "clients";
}

/** Whether the form can be saved: a name, and at least two Mandanten or one Master-Mandant. */
export function canSaveCompanyForm(form: MiraCompanyForm, masters: Set<string> = new Set()): boolean {
  return companyFieldError("name", form) === null && companyFieldError("clientIds", form, masters) === null;
}

/** What a save sends. */
export function toCompanySave(stored: MiraCompany | null, form: MiraCompanyForm): MiraCompanySave {
  return {
    id: stored?.id ?? null,
    name: form.name.trim(),
    clientIds: normalisedIds(form.clientIds),
    retired: form.retired,
    viewingClientId: form.viewingClientId,
    expectedUpdatedAt: stored?.updatedAt ?? null,
  };
}

/** A Mandant's label in the picker and the list: its name, the Kürzel beside it, the id when unnamed. */
export function clientLabel(client: ClientAdministrationRow | undefined, clientId: string): string {
  if (!client) return clientId;
  const name = client.clientName?.trim() || clientId;
  const shorthand = client.iresProjectNumberPrefix?.trim();
  return shorthand ? `${name} (${shorthand})` : name;
}

/**
 * The Mandanten another active company already covers (a group's members included), so the picker can say
 * so before IRES Cloud refuses.
 *
 * @returns client id -> the other company's name.
 */
export function takenClients(companies: MiraCompany[], editingId: string | null): Record<string, string> {
  const taken: Record<string, string> = {};
  for (const company of companies) {
    if (company.retired || company.id === editingId) continue;
    for (const clientId of coveredClients(company)) taken[clientId] = company.name;
  }
  return taken;
}

/**
 * The viewing clients other active companies already show in (#2610112 X6e), so the picker can say so
 * before IRES Cloud refuses.
 *
 * @returns client id -> the other company's name.
 */
export function takenViewingClients(companies: MiraCompany[], editingId: string | null): Record<string, string> {
  const taken: Record<string, string> = {};
  for (const company of companies) {
    if (company.retired || company.id === editingId || !company.viewingClientId) continue;
    taken[company.viewingClientId] = company.name;
  }
  return taken;
}

/** The Mandanten the edited company covers only through its Master-Mandant: they come along, not picked. */
export function membersOfOwnGroup(company: MiraCompany | null): Set<string> {
  if (!company) return new Set();
  const own = new Set(company.clientIds);
  return new Set(coveredClients(company).filter((clientId) => !own.has(clientId)));
}

/** The list rows for a filter and a search over the name and the Mandanten's labels, by name. */
export function filterCompanies(
  companies: MiraCompany[],
  filter: MiraCompanyFilter,
  search: string,
  labelOf: (clientId: string) => string
): MiraCompany[] {
  const term = search.trim().toLowerCase();
  return companies
    .filter((company) => (filter === "ALL" ? true : filter === "RETIRED" ? company.retired : !company.retired))
    .filter((company) => !term || [company.name, ...coveredClients(company).map(labelOf)].some((value) => value.toLowerCase().includes(term)))
    .sort((a, b) => a.name.localeCompare(b.name, "de"));
}

/** A snackbar message: an i18n key and its parameters. */
export interface CompanyErrorMessage {
  key: string;
  params: string[];
}

/**
 * The snackbar message for a refused or failed call, by IRES Cloud's reason. A clash names the first
 * Mandant already in another company.
 */
export function companyErrorMessage(
  error: unknown,
  labelOf: (clientId: string) => string,
  companyNameOf: (companyId: string) => string = (companyId) => companyId
): CompanyErrorMessage {
  const failure = (error ?? {}) as { details?: { reason?: unknown; conflicts?: unknown; companyId?: unknown } | null };
  const reason = typeof failure.details?.reason === "string" ? failure.details.reason : "";
  const conflicts = failure.details?.conflicts;
  if (reason === "CONFLICT" && Array.isArray(conflicts) && conflicts.length > 0) {
    const first = conflicts[0] as { clientId?: unknown };
    return { key: "admin.miraCompanies.errors.CONFLICT", params: [labelOf(String(first.clientId ?? ""))] };
  }
  if (reason === "VIEWER_TAKEN") {
    return { key: "admin.miraCompanies.errors.VIEWER_TAKEN", params: [companyNameOf(String(failure.details?.companyId ?? ""))] };
  }
  const known = [
    "NO_COMPANY",
    "NO_CLIENT",
    "CHANGED_MEANWHILE",
    "INVALID_NAME",
    "INVALID_ACCOUNTS",
    "INVALID_VIEWER",
    "VIEWER_IS_ACCOUNT",
    "VIEWER_IS_GROUP",
  ];
  return { key: known.includes(reason) ? `admin.miraCompanies.errors.${reason}` : "admin.miraCompanies.errors.FAILED", params: [] };
}
